import React, { useEffect, useRef } from "react";
import StyledDiv from "../../../../StyledDiv/StyledDiv";
import {
  CardStickyWrapper,
  StickyCardSection,
} from "../LandingInnovationAdrienCachot.styled";
import useBreakpoint from "../../../../Grid/hooks/useBreakpoint";
import { useSkipHeavyScrollAnimations } from "../../../../../Hooks/useSkipHeavyScrollAnimations";
import { lazyGsap } from "../../../../../lib/gsap/lazyGsap";
import { ILandingInnovationProductAdrienCachot } from "../LandingInnovationAdrienCachot";
import Typography from "../../../../../Foundation/Typography/Typography";

const MOBILE_STICKY_TOP = 128; // px — offset du sticky depuis le top du viewport
/** Ajuste le début du scale (px) : positif = plus tôt, négatif = plus tard. 0 = aligné sur le bas logique de la carte */
const SCALE_SCROLL_START_EXTRA_PX = 0;

const MobileListAdrienCachot = <T extends any = any>({
  title,
  productList,
  itemRenderer,
  containerRef,
}: {
  title?: string;
  productList: ILandingInnovationProductAdrienCachot<T>[];
  itemRenderer: (product: any, index: number) => React.ReactNode;
  containerRef: React.RefObject<HTMLElement | null>;
}) => {
  const { mobile } = useBreakpoint();
  const skipHeavyAnimations = useSkipHeavyScrollAnimations();
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Stack sticky + scrub GSAP — désactivé sur appareils peu performants / reduced-motion
  useEffect(() => {
    if (!mobile || skipHeavyAnimations || typeof window === "undefined") return;

    /** Évite play/pause dans la même tâche que le scrub ScrollTrigger (moins de contention avec le compositeur). */
    let effectCancelled = false;
    let cleanup: (() => void) | undefined;

    lazyGsap().then(({ gsap, ScrollTrigger }) => {
      if (effectCancelled) return;
      const ctx = gsap.context(() => {
        cardRefs.current.forEach((card, i) => {
        if (!card) return;

        // Sticky natif (StickyCardSection + min-height) — pas de pin GSAP

        // Scale down + fade pendant que card N+1 monte sur card N
        const nextCard = cardRefs.current[i + 1];
        if (nextCard) {
          gsap.set(card, {
            transformOrigin: "top center",
            force3D: true,
          });

          const video = card.querySelector<HTMLVideoElement>("video");
          /**
           * Transport scroll → lecture : indépendant du masquage poster dans SushishopVideo
           * (là-bas : double rAF après `loadeddata` pour attendre une frame peinte).
           * Ici on ne change play/pause qu’au passage du seuil 50 %, pas à chaque tick scrub.
           */
          let videoShouldPlay = true;
          let playPauseRaf = 0;

          gsap.to(card, {
            scale: 0.75,
            opacity: 0,
            ease: "none",
            force3D: true,
            scrollTrigger: {
              trigger: nextCard,
              start: () =>
                `top ${
                  card.offsetHeight +
                  MOBILE_STICKY_TOP +
                  SCALE_SCROLL_START_EXTRA_PX
                }px`,
              end: "top top",
              scrub: 0.3,
              fastScrollEnd: true,
              invalidateOnRefresh: true,
              // La card N est couverte par la card N+1 : IntersectionObserver ne le
              // détecte pas (transform ne change pas le bounding rect DOM), on force ici.
              // Pause à 50% de l'animation (card à moitié recouverte), pas au début.
              onUpdate: (self) => {
                if (effectCancelled || !video) return;
                const shouldPlay = self.progress <= 0.5;
                if (shouldPlay === videoShouldPlay) return;
                videoShouldPlay = shouldPlay;
                cancelAnimationFrame(playPauseRaf);
                playPauseRaf = requestAnimationFrame(() => {
                  playPauseRaf = 0;
                  if (effectCancelled || !video) return;
                  if (videoShouldPlay) {
                    video.play().catch(() => {});
                  } else {
                    video.pause();
                  }
                });
              },
            },
          });
        }
        });
        ScrollTrigger.refresh();
      }, containerRef);

      cleanup = () => ctx.revert();
    });

    return () => {
      effectCancelled = true;
      cleanup?.();
    };
  }, [mobile, productList, skipHeavyAnimations]);

  return (
    <StyledDiv styles={{ padding: "0 16px" }}>
      <StyledDiv>
        {title && (
          <StyledDiv styles={{ marginBottom: 16 }}>
            <Typography variant="adriencachot" color="white">
              {title}
            </Typography>
          </StyledDiv>
        )}
      </StyledDiv>
      <StyledDiv
        styles={{
          width: "100%",
          display: "flex",
          flexDirection: "column",

          gap: 16,
        }}
        className="mobile-list-adrien-cachot"
      >
        {productList.map((product, index) =>
          skipHeavyAnimations ? (
            <StyledDiv key={product.id_product} styles={{ width: "100%" }}>
              {itemRenderer(product, index)}
            </StyledDiv>
          ) : (
            <StickyCardSection
              key={product.id_product}
              $isLast={index === productList.length - 1}
            >
              <CardStickyWrapper
                $zIndex={index + 1}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
              >
                {itemRenderer(product, index)}
              </CardStickyWrapper>
            </StickyCardSection>
          )
        )}
      </StyledDiv>
    </StyledDiv>
  );
};

export default MobileListAdrienCachot;
